/**
 * @module toHighlight
 *
 * @see https://0.vuetifyjs.com/composables/transformers/to-highlight
 *
 * @remarks
 * Pure transformer — no DOM, no state, no registry, no reactivity. Splits text
 * into matched and unmatched chunks given a query string, an array of query
 * strings, or pre-computed `[start, end]` match ranges (e.g., from createFilter).
 * Returns a plain array; wrap the call in `computed()` for reactive recomputation.
 *
 * @example
 * ```ts
 * import { toHighlight } from '@vuetify/v0'
 *
 * const chunks = toHighlight('Hello World', 'World')
 * // [{ text: 'Hello ', match: false }, { text: 'World', match: true }]
 * ```
 */

// Transformers
import { toArray } from '#v0/composables/toArray'

// Utilities
import { findMatchRanges } from '#v0/utilities'
import { toValue } from 'vue'

// Types
import type { IgnoreAccents, MatchRange as Range } from '#v0/utilities'
import type { MaybeRefOrGetter } from 'vue'

export type { MatchRange } from '#v0/utilities'

/**
 * A contiguous chunk of source text, flagged as matched or unmatched.
 *
 * @example
 * ```ts
 * import type { HighlightChunk } from '@vuetify/v0'
 *
 * const chunk: HighlightChunk = { text: 'Hello', match: true }
 * ```
 */
export interface HighlightChunk {
  text: string
  match: boolean
}

/**
 * Optional configuration for {@link toHighlight}.
 *
 * @example
 * ```ts
 * import { toHighlight } from '@vuetify/v0'
 *
 * const chunks = toHighlight('Hello World', 'WORLD', {
 *   ignoreCase: true,
 *   matchAll: true,
 * })
 * ```
 */
export interface ToHighlightOptions {
  /**
   * Pre-computed `[start, end]` index pairs.
   * When non-empty, takes priority over `query`. `matchAll` is ignored.
   * Caller-supplied ranges are sorted and merged before chunking, so
   * unsorted or overlapping input is handled gracefully.
   */
  matches?: MaybeRefOrGetter<readonly Range[] | undefined>
  /**
   * Highlight every occurrence (`true`) or only the first per term (`false`, default).
   * Ignored when `matches` is provided.
   */
  matchAll?: MaybeRefOrGetter<boolean>
  /** Case-insensitive matching. Default `false`. */
  ignoreCase?: MaybeRefOrGetter<boolean>
  /**
   * Folds accents before matching. `'query'` folds only the search term,
   * `'target'` only the text, `true` both sides. Default `false`.
   */
  ignoreAccents?: MaybeRefOrGetter<IgnoreAccents>
}

function merge (ranges: readonly Range[]): Range[] {
  const sorted = ranges
    .filter(span => span[0] < span[1])
    .toSorted((a, b) => a[0] - b[0])
  const merged: [number, number][] = []

  for (const span of sorted) {
    const last = merged.at(-1)
    if (last && span[0] <= last[1]) last[1] = Math.max(last[1], span[1])
    else merged.push([span[0], span[1]])
  }

  return merged
}

function chunk (text: string, ranges: readonly Range[]): HighlightChunk[] {
  const chunks: HighlightChunk[] = []
  let cursor = 0

  for (const [start, end] of ranges) {
    if (cursor < start) chunks.push({ text: text.slice(cursor, start), match: false })
    chunks.push({ text: text.slice(start, end), match: true })
    cursor = end
  }

  if (cursor < text.length) chunks.push({ text: text.slice(cursor), match: false })

  return chunks
}

function find (
  text: string,
  query: string | string[],
  options: { matchAll: boolean, ignoreCase: boolean, ignoreAccents: IgnoreAccents },
): Range[] {
  const spans: Range[] = []

  for (const term of toArray(query).filter(Boolean)) {
    spans.push(...findMatchRanges(text, term, options))
  }

  return merge(spans)
}

/**
 * Splits text into matched and unmatched chunks.
 *
 * Pure transformer — returns a plain array. Wrap the call in `computed()` for
 * reactive recomputation.
 *
 * Priority: `options.matches` (when non-empty) → `query` → no-match fallback.
 *
 * @param text The source string to split.
 * @param query One or more search terms. Empty strings are ignored. Case sensitivity controlled by `options.ignoreCase`.
 * @param options Optional `matches`, `matchAll`, `ignoreCase`, `ignoreAccents`.
 * @returns A `HighlightChunk[]` array.
 *
 * @see https://0.vuetifyjs.com/composables/transformers/to-highlight
 *
 * @example
 * ```ts
 * import { computed, shallowRef } from 'vue'
 * import { toHighlight } from '@vuetify/v0'
 *
 * const query = shallowRef('World')
 * const chunks = computed(() => toHighlight('Hello World', query))
 *
 * console.log(chunks.value)
 * // [{ text: 'Hello ', match: false }, { text: 'World', match: true }]
 * ```
 */
/* #__NO_SIDE_EFFECTS__ */
export function toHighlight (
  text: MaybeRefOrGetter<string>,
  query?: MaybeRefOrGetter<string | string[] | undefined>,
  options: ToHighlightOptions = {},
): HighlightChunk[] {
  const _text = toValue(text)
  const _query = toValue(query)
  const _matches = toValue(options.matches)
  const matchAll = toValue(options.matchAll) ?? false
  const ignoreCase = toValue(options.ignoreCase) ?? false
  const ignoreAccents = toValue(options.ignoreAccents) ?? false

  if (_matches?.length) return chunk(_text, merge(_matches))

  if (_query) {
    const ranges = find(_text, _query, { matchAll, ignoreCase, ignoreAccents })
    return ranges.length > 0 ? chunk(_text, ranges) : [{ text: _text, match: false }]
  }

  return [{ text: _text, match: false }]
}
